import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  withStyles,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';
import { pdfjs, Document, Page } from 'react-pdf';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const options = {
  cMapUrl: '/cmaps/',
  standardFontDataUrl: '/standard_fonts/'
};

const PdfViewer = ({ url }) => {
  const [numPages, setNumPages] = useState();
  const [pageWidth, setPageWidth] = useState(null);
  const containerRef = React.useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const onDocumentLoadSuccess = ({ numPages: nextNumPages }) => {
    setNumPages(nextNumPages);
  };

  // Calculate page width to match the modal's content width
  const calculatePageWidth = () => {
    if (containerRef.current) {
      // Use the full width of the container minus padding
      const containerWidth = containerRef.current.clientWidth;
      const padding = isMobile ? 16 : 32; // Account for padding
      return containerWidth - padding;
    }

    // Fallback calculation based on modal sizes
    if (isMobile) {
      return window.innerWidth * 0.95 - 32; // 95vw minus padding
    } else if (isTablet) {
      return window.innerWidth * 0.85 - 32; // 85vw minus padding
    } else {
      return window.innerWidth * 0.75 - 48; // 75vw minus padding
    }
  };

  // Update page width on mount and window resize
  React.useEffect(() => {
    const updateWidth = () => {
      // Add a small delay to ensure the container is fully rendered
      setTimeout(() => {
        setPageWidth(calculatePageWidth());
      }, 100);
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [isMobile, isTablet]);

  return (
    <div
      ref={containerRef}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        padding: isMobile ? '8px' : '16px'
      }}>
      <Document
        file={url}
        onLoadSuccess={onDocumentLoadSuccess}
        loading={
          <div
            style={{
              padding: '20px',
              textAlign: 'center',
              color: theme.palette.text.secondary
            }}>
            Loading PDF...
          </div>
        }
        error={
          <div
            style={{
              padding: '20px',
              textAlign: 'center',
              color: theme.palette.error.main
            }}>
            Failed to load PDF. Please try again.
          </div>
        }
        options={options}>
        {Array.from(new Array(numPages), (el, index) => (
          <Page
            key={`page_${index + 1}`}
            pageNumber={index + 1}
            width={pageWidth || undefined}
            scale={pageWidth ? undefined : 0.9}
            style={{
              marginBottom: isMobile ? 8 : 16,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              borderRadius: '4px',
              maxWidth: '100%',
              width: '100%'
            }}
          />
        ))}
      </Document>
    </div>
  );
};

const styles = (theme) => ({
  dialogPaper: {
    width: '95vw',
    height: '95vh',
    maxWidth: '95vw',
    maxHeight: '95vh',
    padding: 8
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    // Mobile adjustments
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1)
    }
  },
  dialogContent: {
    padding: 0,
    overflow: 'auto',
    height: 'calc(100% - 64px)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: theme.palette.grey[50],
    width: '100%',
    // Mobile adjustments
    [theme.breakpoints.down('sm')]: {
      height: 'calc(100% - 56px)',
      backgroundColor: '#fff'
    }
  },
  closeButton: {
    color: theme.palette.grey[500],
    // Mobile size adjustment
    position: 'absolute',
    right: 2,
    top: 2,
    [theme.breakpoints.down('sm')]: {
      padding: 4
    }
  }
});

const PdfModal = ({ open, onClose, pdfUrl, lessonName, classes }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullScreen={isMobile}
      classes={{ paper: classes.dialogPaper }}>
      <DialogTitle className={classes.dialogTitle}>
        <Typography variant={isMobile ? 'h6' : 'h5'}>
          {lessonName || 'Lesson Material'}
        </Typography>
        <IconButton
          className={classes.closeButton}
          onClick={onClose}
          size={isMobile ? 'small' : 'medium'}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {pdfUrl ? (
          <PdfViewer url={pdfUrl} />
        ) : (
          <div
            style={{
              padding: '20px',
              textAlign: 'center',
              color: theme.palette.text.secondary
            }}>
            Loading PDF...
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default withStyles(styles)(PdfModal);
